import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  getDocs,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import type { ActiveTimer, CreateActiveTimer, UpdateActiveTimer } from '../types/activeTimer';

const COLLECTION = 'activeTimers';

/**
 * Crear un nuevo timer activo en Firestore
 */
export async function createActiveTimer(data: CreateActiveTimer): Promise<string> {
  try {
    const docRef = await addDoc(collection(db, COLLECTION), {
      ...data,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return docRef.id;
  } catch (error) {
    throw error;
  }
}

/**
 * Actualizar un timer existente
 */
export async function updateActiveTimer(
  timerId: string,
  updates: UpdateActiveTimer
): Promise<void> {
  try {
    const timerRef = doc(db, COLLECTION, timerId);
    await updateDoc(timerRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    throw error;
  }
}

/**
 * Eliminar un timer (cuando se completa o cancela)
 */
export async function deleteActiveTimer(timerId: string): Promise<void> {
  try {
    const timerRef = doc(db, COLLECTION, timerId);
    await deleteDoc(timerRef);
  } catch (error) {
    throw error;
  }
}

/**
 * Obtener todos los timers activos de una familia
 */
export async function getFamilyActiveTimers(familyId: string): Promise<ActiveTimer[]> {
  try {
    const q = query(
      collection(db, COLLECTION),
      where('familyId', '==', familyId)
    );
    
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as ActiveTimer[];
  } catch (error) {
    return [];
  }
}

/**
 * Obtener timers activos de un miembro específico
 */
export async function getMemberActiveTimers(
  familyId: string,
  memberId: string
): Promise<ActiveTimer[]> {
  try {
    const q = query(
      collection(db, COLLECTION),
      where('familyId', '==', familyId),
      where('memberId', '==', memberId)
    );
    
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as ActiveTimer[];
  } catch (error) {
    return [];
  }
}

/**
 * Buscar timer existente para una actividad específica de un miembro
 */
export async function findExistingTimer(
  familyId: string,
  memberId: string,
  activityId: string,
  startedPeriod?: string
): Promise<ActiveTimer | null> {
  try {
    const constraints: any[] = [
      where('familyId', '==', familyId),
      where('memberId', '==', memberId),
      where('activityId', '==', activityId),
    ];

    // Si se proporciona un período/horario, scopear el timer a esa instancia.
    // Esto permite múltiples timers por actividad cuando la actividad se muestra en varios horarios.
    if (startedPeriod) {
      constraints.push(where('startedPeriod', '==', startedPeriod));
    }

    const q = query(collection(db, COLLECTION), ...constraints);
    
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) return null;
    
    const doc = snapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data(),
    } as ActiveTimer;
  } catch (error) {
    return null;
  }
}

/**
 * Pausar un timer (actualizar accumulatedSec y detener)
 */
export async function pauseTimer(timer: ActiveTimer): Promise<void> {
  if (!timer.running) return;
  
  const now = Date.now();
  const startedAtMs = timer.startedAt.toMillis();
  const elapsedSec = Math.floor((now - startedAtMs) / 1000);
  
  await updateActiveTimer(timer.id, {
    running: false,
    accumulatedSec: timer.accumulatedSec + elapsedSec,
  });
}

/**
 * Reanudar un timer pausado
 */
export async function resumeTimer(timer: ActiveTimer): Promise<void> {
  if (timer.running) return;
  
  await updateActiveTimer(timer.id, {
    running: true,
    startedAt: Timestamp.now(),
  });
}

/**
 * Calcular estado actual del timer
 */
export function calculateTimerState(timer: ActiveTimer, nowMs: number): {
  totalElapsedSec: number;
  remainingSec: number;
  phase: 'green' | 'amber' | 'red' | null;
} {
  const startedAtMs = timer.startedAt.toMillis();
  const runningElapsed = timer.running ? Math.floor((nowMs - startedAtMs) / 1000) : 0;
  const totalElapsedSec = timer.accumulatedSec + runningElapsed;
  
  const durationSec = timer.durationMinutes * 60;
  const graceSec = timer.graceMinutes * 60;
  const remainingSec = durationSec - totalElapsedSec;
  
  let phase: 'green' | 'amber' | 'red' | null = null;
  
  if (timer.running) {
    if (timer.completionGoal === 'complete_time') {
      // Siempre verde mientras corre (objetivo es cumplir tiempo mínimo)
      phase = 'green';
    } else {
      // Semáforo tradicional para 'before_time'
      if (remainingSec > 0) {
        phase = 'green';
      } else if (remainingSec >= -graceSec) {
        phase = 'amber';
      } else {
        phase = 'red';
      }
    }
  }
  
  return { totalElapsedSec, remainingSec, phase };
}
